import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Plus, Trash2, ChevronLeft, AlertTriangle, MapPin, PlusCircle } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import PageHeader from "../components/PageHeader";
import Modal from "../components/Modal";
import QuickAddProductModal from "../components/QuickAddProductModal";
import { fmtMoney } from "../lib/format";

function addDaysIso(iso, days) {
    if (!iso) return "";
    const d = new Date(iso + "T00:00:00Z");
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
}

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function OrderNew() {
    const navigate = useNavigate();
    const [customers, setCustomers] = useState([]);
    const [products, setProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);

    const [customerId, setCustomerId] = useState("");
    const [customerAddresses, setCustomerAddresses] = useState([]);
    const [customerContacts, setCustomerContacts] = useState([]);
    const [shipToAddressId, setShipToAddressId] = useState("");
    const [shippingMethod, setShippingMethod] = useState("");
    const [orderedByContactId, setOrderedByContactId] = useState("");
    const [showAddShipTo, setShowAddShipTo] = useState(false);
    const [showAddContact, setShowAddContact] = useState(false);
    const [customerPo, setCustomerPo] = useState("");
    const [poDate, setPoDate] = useState(todayIso());
    const [eta, setEta] = useState(addDaysIso(todayIso(), 15));
    const [etaTouched, setEtaTouched] = useState(false);
    const [blindShipment, setBlindShipment] = useState(false);
    const [productionType, setProductionType] = useState("in_house");
    const [notes, setNotes] = useState("");
    const [lines, setLines] = useState([]);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [dupWarning, setDupWarning] = useState(null);
    // Round-30 — Quick add product; lineIdxForNewProduct is the row index
    // that should receive the newly-created product on the current form.
    const [showQuickAddProduct, setShowQuickAddProduct] = useState(false);
    const [lineIdxForNewProduct, setLineIdxForNewProduct] = useState(null);

    useEffect(() => {
        api.get("/customers", { params: { active: true } }).then((r) => setCustomers(r.data));
    }, []);

    useEffect(() => {
        if (!customerId) {
            setProducts([]);
            setCustomerAddresses([]);
            setCustomerContacts([]);
            setShipToAddressId("");
            setOrderedByContactId("");
            setShippingMethod("");
            return;
        }
        setLoadingProducts(true);
        api.get("/products", { params: { customer_id: customerId, active: true } })
            .then((r) => setProducts(r.data))
            .finally(() => setLoadingProducts(false));
        // Load customer (with addresses + contacts) for Ship-To / Ordered-By selectors
        api.get(`/customers/${customerId}`).then((r) => {
            const addrs = r.data?.addresses || [];
            setCustomerAddresses(addrs);
            const primary = addrs.find((a) => a.is_primary) || addrs[0];
            setShipToAddressId(primary ? primary.id : "");
            const contacts = (r.data?.contacts || []).filter((c) => c.active !== false);
            setCustomerContacts(contacts);
            const primaryContact = contacts.find((c) => c.is_primary) || contacts[0];
            setOrderedByContactId(primaryContact ? primaryContact.id : "");
        });
        setLines([]);
    }, [customerId]);

    // ETA auto-derives from PO date until user manually edits ETA
    useEffect(() => {
        if (!etaTouched) {
            setEta(addDaysIso(poDate, 15));
        }
    }, [poDate, etaTouched]);

    const productById = useMemo(
        () => Object.fromEntries(products.map((p) => [p.id, p])),
        [products],
    );

    const addLine = () => {
        if (!products.length) return;
        const p = products[0];
        setLines((ls) => [
            ...ls,
            {
                _rid: (typeof crypto !== "undefined" && crypto.randomUUID
                    ? crypto.randomUUID()
                    : `ln-${Date.now()}-${ls.length}`),
                product_id: p.id,
                qty_ordered: 1000,
                price: p.price || "0.0000",
            },
        ]);
    };

    // After a quick-add creates a new product, add it to the local list AND
    // either set it on the current row (if we opened from a specific line)
    // or create a fresh line pre-filled with it.
    const onProductCreated = (product) => {
        setProducts((prev) => [...prev, product]);
        setLines((ls) => {
            if (
                lineIdxForNewProduct !== null &&
                lineIdxForNewProduct >= 0 &&
                lineIdxForNewProduct < ls.length
            ) {
                return ls.map((l, i) =>
                    i === lineIdxForNewProduct
                        ? {
                              ...l,
                              product_id: product.id,
                              price: product.price || "0.0000",
                          }
                        : l,
                );
            }
            return [
                ...ls,
                {
                    _rid: (typeof crypto !== "undefined" && crypto.randomUUID
                        ? crypto.randomUUID()
                        : `ln-${Date.now()}-${ls.length}`),
                    product_id: product.id,
                    qty_ordered: 1000,
                    price: product.price || "0.0000",
                },
            ];
        });
        setLineIdxForNewProduct(null);
    };

    const updateLine = (idx, patch) =>
        setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

    const removeLine = (idx) => setLines((ls) => ls.filter((_, i) => i !== idx));

    const orderTotal = useMemo(() => {
        return lines.reduce((sum, l) => {
            const q = Number(l.qty_ordered) || 0;
            const p = Number(l.price) || 0;
            return sum + q * p;
        }, 0);
    }, [lines]);

    const validate = () => {
        if (!customerId) return "Select a customer.";
        if (!shipToAddressId)
            return "Select a Ship-To address (or add one if the PO ships elsewhere).";
        if (!shippingMethod) return "Select a Shipping Method (CPU / LTL / Shipping).";
        if (!orderedByContactId) return "Select Ordered By (contact who placed the order).";
        if (!customerPo.trim()) return "Customer PO is required.";
        if (!poDate) return "PO date is required.";
        if (!eta) return "ETA is required.";
        if (lines.length === 0) return "Add at least one product line.";
        for (const l of lines) {
            if (!l.product_id) return "Each line must have a product.";
            if (!l.qty_ordered || Number(l.qty_ordered) < 1)
                return "Quantity must be at least 1.";
        }
        return null;
    };

    const submit = async (override = false) => {
        const v = validate();
        if (v) {
            setError(v);
            return;
        }
        setError("");
        setSubmitting(true);
        try {
            const payload = {
                customer_id: customerId,
                ship_to_address_id: shipToAddressId,
                shipping_method: shippingMethod,
                ordered_by_contact_id: orderedByContactId,
                customer_po: customerPo.trim(),
                po_date: poDate,
                eta,
                blind_shipment: blindShipment,
                production_type: productionType,
                notes,
                duplicate_po_override: override,
                lines: lines.map((l) => ({
                    product_id: l.product_id,
                    qty_ordered: Number(l.qty_ordered),
                    price: String(l.price),
                })),
            };
            const { data } = await api.post("/orders", payload);
            navigate(`/orders/${data.id}`);
        } catch (err) {
            if (err?.response?.status === 409) {
                // Duplicate PO — show override dialog
                try {
                    const r = await api.post("/orders/check-duplicate-po", {
                        customer_id: customerId,
                        customer_po: customerPo.trim(),
                    });
                    setDupWarning(r.data);
                } catch {
                    setError(formatApiError(err?.response?.data?.detail));
                }
            } else {
                setError(formatApiError(err?.response?.data?.detail) || err.message);
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="order-new-page">
            <Link
                to="/orders"
                className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-2"
            >
                <ChevronLeft className="w-4 h-4" /> Back to Orders
            </Link>
            <PageHeader title="New Order" subtitle="Create an operational order" />

            {error && (
                <div className="text-sm border border-red-200 bg-red-50 text-red-700 rounded-sm px-3 py-2 mb-4">
                    {error}
                </div>
            )}

            <div className="bg-white border border-gray-200 rounded-sm p-4 mb-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                            Customer
                        </label>
                        <select
                            value={customerId}
                            onChange={(e) => setCustomerId(e.target.value)}
                            className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm bg-white"
                            data-testid="order-customer"
                        >
                            <option value="">— Select customer —</option>
                            {customers.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name}
                                </option>
                            ))}
                        </select>
                        <Link
                            to="/customers"
                            className="text-xs text-red-700 hover:text-red-900 mt-1 inline-block"
                        >
                            + Add new customer
                        </Link>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                            Customer PO #
                        </label>
                        <input
                            value={customerPo}
                            onChange={(e) => setCustomerPo(e.target.value)}
                            className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm odr-mono"
                            data-testid="order-po"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                            PO Date
                        </label>
                        <input
                            type="date"
                            value={poDate}
                            onChange={(e) => setPoDate(e.target.value)}
                            className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm"
                            data-testid="order-po-date"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                            ETA <span className="text-gray-400 normal-case">· default +15d</span>
                        </label>
                        <input
                            type="date"
                            value={eta}
                            onChange={(e) => {
                                setEta(e.target.value);
                                setEtaTouched(true);
                            }}
                            className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm"
                            data-testid="order-eta"
                        />
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                            Ship-To Address <span className="text-red-600">*</span>
                        </label>
                        <div className="flex gap-2 items-stretch">
                            <select
                                value={shipToAddressId}
                                onChange={(e) => setShipToAddressId(e.target.value)}
                                disabled={!customerId}
                                className="odr-input flex-1 rounded-sm border border-gray-300 px-3 text-sm bg-white disabled:bg-gray-100"
                                data-testid="order-ship-to"
                            >
                                <option value="">
                                    {customerId
                                        ? customerAddresses.length === 0
                                            ? "— No saved addresses — add one →"
                                            : "— Select Ship-To —"
                                        : "Select customer first"}
                                </option>
                                {customerAddresses.map((a) => {
                                    const parts = [a.address1, a.city, a.state, a.postal_code]
                                        .filter(Boolean)
                                        .join(", ");
                                    return (
                                        <option key={a.id} value={a.id}>
                                            {a.label ? `${a.label} — ` : ""}
                                            {parts}
                                            {a.is_primary ? "  (primary)" : ""}
                                        </option>
                                    );
                                })}
                            </select>
                            <button
                                type="button"
                                disabled={!customerId}
                                onClick={() => setShowAddShipTo(true)}
                                className="odr-btn inline-flex items-center gap-1 rounded-sm border border-gray-300 px-3 text-xs font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                                data-testid="order-ship-to-add"
                                title="Add Ship-To Address"
                            >
                                <MapPin className="w-3.5 h-3.5" /> Add
                            </button>
                        </div>
                        {shipToAddressId &&
                            (() => {
                                const a = customerAddresses.find(
                                    (x) => x.id === shipToAddressId,
                                );
                                if (!a) return null;
                                return (
                                    <div
                                        className="text-[11px] text-gray-600 mt-1 odr-mono"
                                        data-testid="order-ship-to-preview"
                                    >
                                        {a.address1}
                                        {a.address2 ? `, ${a.address2}` : ""}
                                        {a.city || a.state || a.postal_code
                                            ? ` · ${[a.city, a.state, a.postal_code].filter(Boolean).join(" ")}`
                                            : ""}
                                    </div>
                                );
                            })()}
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                            Shipping Method <span className="text-red-600">*</span>
                        </label>
                        <select
                            value={shippingMethod}
                            onChange={(e) => setShippingMethod(e.target.value)}
                            disabled={!customerId}
                            className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm bg-white disabled:bg-gray-100"
                            data-testid="order-shipping-method"
                        >
                            <option value="">— Select method —</option>
                            <option value="CPU">CPU — Customer Pick-Up</option>
                            <option value="LTL">LTL — Less-than-Truckload</option>
                            <option value="Shipping">Shipping</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                            Ordered By <span className="text-red-600">*</span>
                        </label>
                        <div className="flex gap-2 items-stretch">
                            <select
                                value={orderedByContactId}
                                onChange={(e) => setOrderedByContactId(e.target.value)}
                                disabled={!customerId}
                                className="odr-input flex-1 rounded-sm border border-gray-300 px-3 text-sm bg-white disabled:bg-gray-100"
                                data-testid="order-ordered-by"
                            >
                                <option value="">
                                    {customerId
                                        ? customerContacts.length === 0
                                            ? "— No saved contacts — add one →"
                                            : "— Select contact —"
                                        : "Select customer first"}
                                </option>
                                {customerContacts.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.full_name}
                                        {c.email ? ` · ${c.email}` : ""}
                                        {c.is_primary ? "  (primary)" : ""}
                                    </option>
                                ))}
                            </select>
                            <button
                                type="button"
                                disabled={!customerId}
                                onClick={() => setShowAddContact(true)}
                                className="odr-btn inline-flex items-center gap-1 rounded-sm border border-gray-300 px-3 text-xs font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                                data-testid="order-ordered-by-add"
                                title="Add Contact"
                            >
                                <Plus className="w-3.5 h-3.5" /> Add
                            </button>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                            Production Type <span className="text-red-600">*</span>
                        </label>
                        <select
                            value={productionType}
                            onChange={(e) => setProductionType(e.target.value)}
                            className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm bg-white"
                            data-testid="order-production-type"
                        >
                            <option value="in_house">In-House</option>
                            <option value="farm_out">Farmed Out</option>
                        </select>
                        <p className="text-[10px] text-gray-500 mt-1">
                            Editable at any time from the order page after creation.
                        </p>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                            Notes
                        </label>
                        <textarea
                            rows={2}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm"
                            data-testid="order-notes"
                        />
                    </div>
                </div>
                <label className="inline-flex items-center gap-2 text-sm text-gray-800">
                    <input
                        type="checkbox"
                        checked={blindShipment}
                        onChange={(e) => setBlindShipment(e.target.checked)}
                        data-testid="order-blind"
                    />
                    Blind Shipment (strips Pro-Pac branding from all documents)
                </label>
            </div>

            <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
                <div className="flex items-center justify-between p-3 border-b border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-900">Line Items</h3>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => {
                                setLineIdxForNewProduct(null);
                                setShowQuickAddProduct(true);
                            }}
                            disabled={!customerId}
                            className="odr-btn inline-flex items-center gap-2 rounded-sm border border-gray-300 px-3 text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                            data-testid="order-add-new-product"
                            title="Create a new product under this customer without leaving the order"
                        >
                            <PlusCircle className="w-4 h-4" /> Add New Product
                        </button>
                        <button
                            onClick={addLine}
                            disabled={!customerId || !products.length}
                            className="odr-btn inline-flex items-center gap-2 rounded-sm border border-gray-300 px-3 text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                            data-testid="order-add-line"
                        >
                            <Plus className="w-4 h-4" /> Add line
                        </button>
                    </div>
                </div>
                <table className="odr-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Product</th>
                            <th className="text-right">Qty</th>
                            <th className="text-right">Price</th>
                            <th className="text-right">Line Value</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {lines.length === 0 && (
                            <tr>
                                <td colSpan={6} className="text-center text-gray-500 py-6">
                                    {!customerId ? (
                                        "Select a customer to load products."
                                    ) : loadingProducts ? (
                                        "Loading products…"
                                    ) : products.length === 0 ? (
                                        <>
                                            <div className="mb-2">
                                                This customer has no products yet.
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setLineIdxForNewProduct(null);
                                                    setShowQuickAddProduct(true);
                                                }}
                                                className="inline-flex items-center gap-2 rounded-sm bg-red-700 text-white px-3 py-1.5 text-sm font-semibold hover:bg-red-800"
                                                data-testid="order-empty-add-product"
                                            >
                                                <PlusCircle className="w-4 h-4" />
                                                Add New Product for this Customer
                                            </button>
                                        </>
                                    ) : (
                                        'Click "Add line" to start.'
                                    )}
                                </td>
                            </tr>
                        )}
                        {lines.map((l, idx) => {
                            const prod = productById[l.product_id];
                            const orig = prod ? Number(prod.price) : null;
                            const priceChanged =
                                prod && Number(l.price) !== orig && orig !== null;
                            return (
                                <tr key={l._rid || `line-${idx}`} data-testid={`order-line-${idx}`}>
                                    <td className="odr-mono">{idx + 1}</td>
                                    <td>
                                        <div className="flex items-stretch gap-1">
                                            <select
                                                value={l.product_id}
                                                onChange={(e) => {
                                                    const p = productById[e.target.value];
                                                    updateLine(idx, {
                                                        product_id: e.target.value,
                                                        price: p?.price || "0.0000",
                                                    });
                                                }}
                                                className="odr-input flex-1 min-w-[220px] rounded-sm border border-gray-300 px-2 text-sm bg-white"
                                                data-testid={`order-line-product-${idx}`}
                                            >
                                                {products.map((p) => (
                                                    <option key={p.id} value={p.id}>
                                                        {p.name}
                                                        {p.category_name_cached
                                                            ? ` · ${p.category_name_cached}`
                                                            : ""}
                                                    </option>
                                                ))}
                                            </select>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setLineIdxForNewProduct(idx);
                                                    setShowQuickAddProduct(true);
                                                }}
                                                className="odr-btn inline-flex items-center rounded-sm border border-gray-300 px-2 text-xs text-gray-800 hover:bg-gray-50"
                                                title="Add a new product and use it here"
                                                data-testid={`order-line-add-product-${idx}`}
                                            >
                                                <PlusCircle className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </td>
                                    <td className="text-right">
                                        <input
                                            type="number"
                                            min="1"
                                            value={l.qty_ordered}
                                            onChange={(e) =>
                                                updateLine(idx, { qty_ordered: e.target.value })
                                            }
                                            className="odr-input w-24 rounded-sm border border-gray-300 px-2 text-sm text-right odr-mono"
                                            data-testid={`order-line-qty-${idx}`}
                                        />
                                    </td>
                                    <td className="text-right">
                                        <input
                                            type="text"
                                            inputMode="decimal"
                                            value={l.price}
                                            onChange={(e) =>
                                                updateLine(idx, { price: e.target.value })
                                            }
                                            className="odr-input w-28 rounded-sm border border-gray-300 px-2 text-sm text-right odr-mono"
                                            data-testid={`order-line-price-${idx}`}
                                        />
                                        {priceChanged && (
                                            <div
                                                className="text-[10px] text-amber-700 mt-0.5"
                                                title="Price differs from product. Will update product price + audit on save."
                                            >
                                                ↳ updates product
                                            </div>
                                        )}
                                    </td>
                                    <td className="text-right odr-mono text-sm">
                                        {fmtMoney(
                                            (Number(l.qty_ordered) || 0) *
                                                (Number(l.price) || 0),
                                        )}
                                    </td>
                                    <td className="text-right">
                                        <button
                                            onClick={() => removeLine(idx)}
                                            className="text-gray-500 hover:text-red-700"
                                            data-testid={`order-line-remove-${idx}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                        {lines.length > 0 && (
                            <tr className="bg-gray-50">
                                <td colSpan={4} className="text-right font-semibold">
                                    Total
                                </td>
                                <td className="text-right font-bold odr-mono">
                                    {fmtMoney(orderTotal)}
                                </td>
                                <td></td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="flex justify-end gap-2 mt-4">
                <Link
                    to="/orders"
                    className="odr-btn rounded-sm border border-gray-300 px-4 text-sm text-gray-700 hover:bg-gray-50"
                >
                    Cancel
                </Link>
                <button
                    onClick={() => submit(false)}
                    disabled={submitting}
                    className="odr-btn rounded-sm bg-[var(--odr-red)] hover:bg-[var(--odr-red-hover)] text-white text-sm font-semibold px-4 disabled:opacity-60"
                    data-testid="order-submit"
                >
                    {submitting ? "Saving…" : "Create Order & Generate Confirmation"}
                </button>
            </div>

            {showAddShipTo && (
                <AddShipToModal
                    customerId={customerId}
                    onClose={() => setShowAddShipTo(false)}
                    onAdded={(newAddr, refreshedCustomer) => {
                        setCustomerAddresses(refreshedCustomer.addresses || []);
                        setShipToAddressId(newAddr.id);
                        setShowAddShipTo(false);
                    }}
                />
            )}

            {showAddContact && (
                <AddContactModal
                    customerId={customerId}
                    onClose={() => setShowAddContact(false)}
                    onAdded={(newContact, refreshedCustomer) => {
                        setCustomerContacts(
                            (refreshedCustomer.contacts || []).filter(
                                (c) => c.active !== false,
                            ),
                        );
                        setOrderedByContactId(newContact.id);
                        setShowAddContact(false);
                    }}
                />
            )}

            {showQuickAddProduct && (
                <QuickAddProductModal
                    customerId={customerId}
                    customerName={
                        customers.find((c) => c.id === customerId)?.name || ""
                    }
                    onClose={() => {
                        setShowQuickAddProduct(false);
                        setLineIdxForNewProduct(null);
                    }}
                    onCreated={(product) => {
                        onProductCreated(product);
                    }}
                />
            )}

            {dupWarning && (
                <Modal
                    title="Duplicate PO detected"
                    onClose={() => setDupWarning(null)}
                    testId="dup-po-modal"
                >
                    <div className="space-y-3">
                        <div className="flex gap-3 items-start text-sm text-gray-800">
                            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                            <div>
                                <p>
                                    This customer already has an order with PO{" "}
                                    <span className="odr-mono font-semibold">
                                        {customerPo}
                                    </span>
                                    :
                                </p>
                                <ul className="list-disc ml-5 mt-2 text-xs">
                                    {dupWarning.existing_orders?.map((o) => (
                                        <li key={o.id} className="odr-mono">
                                            {o.internal_number} — {o.status} (PO date{" "}
                                            {o.po_date})
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-2 text-xs text-gray-600">
                                    Override will create another order with the same PO and
                                    record the override in the audit trail.
                                </p>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                            <button
                                onClick={() => setDupWarning(null)}
                                className="odr-btn rounded-sm border border-gray-300 px-4 text-sm text-gray-700 hover:bg-gray-50"
                                data-testid="dup-po-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    setDupWarning(null);
                                    submit(true);
                                }}
                                className="odr-btn rounded-sm bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-4"
                                data-testid="dup-po-override"
                            >
                                Proceed Anyway
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

function AddShipToModal({ customerId, onClose, onAdded }) {
    const [label, setLabel] = useState("");
    const [address1, setAddress1] = useState("");
    const [address2, setAddress2] = useState("");
    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [postal, setPostal] = useState("");
    const [country, setCountry] = useState("US");
    const [isPrimary, setIsPrimary] = useState(false);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    const submit = async () => {
        if (!address1.trim()) {
            setErr("Address line 1 is required.");
            return;
        }
        setSaving(true);
        setErr("");
        try {
            const { data } = await api.post(`/customers/${customerId}/addresses`, {
                label: label.trim(),
                address1: address1.trim(),
                address2: address2.trim(),
                city: city.trim(),
                state: state.trim(),
                postal_code: postal.trim(),
                country: country.trim() || "US",
                is_primary: isPrimary,
            });
            onAdded(data.address, data.customer);
        } catch (e) {
            setErr(formatApiError(e?.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal title="Add Ship-To Address" onClose={onClose} testId="add-ship-to-modal">
            <div className="space-y-3">
                {err && (
                    <div className="text-sm border border-red-200 bg-red-50 text-red-700 rounded-sm px-3 py-2">
                        {err}
                    </div>
                )}
                <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                        Label <span className="text-gray-400 normal-case">(optional)</span>
                    </label>
                    <input
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        placeholder="Warehouse #2, East Dock, etc."
                        className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm"
                        data-testid="ship-to-label"
                    />
                </div>
                <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                        Address Line 1 <span className="text-red-600">*</span>
                    </label>
                    <input
                        value={address1}
                        onChange={(e) => setAddress1(e.target.value)}
                        className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm"
                        data-testid="ship-to-address1"
                    />
                </div>
                <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                        Address Line 2
                    </label>
                    <input
                        value={address2}
                        onChange={(e) => setAddress2(e.target.value)}
                        className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm"
                        data-testid="ship-to-address2"
                    />
                </div>
                <div className="grid grid-cols-3 gap-2">
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                            City
                        </label>
                        <input
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                            className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm"
                            data-testid="ship-to-city"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                            State
                        </label>
                        <input
                            value={state}
                            onChange={(e) => setState(e.target.value)}
                            className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm odr-mono"
                            data-testid="ship-to-state"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                            Postal Code
                        </label>
                        <input
                            value={postal}
                            onChange={(e) => setPostal(e.target.value)}
                            className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm odr-mono"
                            data-testid="ship-to-postal"
                        />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-2 items-end">
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                            Country
                        </label>
                        <input
                            value={country}
                            onChange={(e) => setCountry(e.target.value)}
                            className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm odr-mono"
                            data-testid="ship-to-country"
                        />
                    </div>
                    <label className="inline-flex items-center gap-2 text-sm text-gray-800 pb-2">
                        <input
                            type="checkbox"
                            checked={isPrimary}
                            onChange={(e) => setIsPrimary(e.target.checked)}
                            data-testid="ship-to-primary"
                        />
                        Set as primary
                    </label>
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={onClose}
                        className="odr-btn rounded-sm border border-gray-300 px-4 text-sm text-gray-700 hover:bg-gray-50"
                        data-testid="ship-to-cancel"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={saving}
                        className="odr-btn rounded-sm bg-[var(--odr-red)] hover:bg-[var(--odr-red-hover)] text-white text-sm font-semibold px-4 disabled:opacity-60"
                        data-testid="ship-to-save"
                    >
                        {saving ? "Saving…" : "Save Address"}
                    </button>
                </div>
            </div>
        </Modal>
    );
}


function AddContactModal({ customerId, onClose, onAdded }) {
    const [fullName, setFullName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [isPrimary, setIsPrimary] = useState(false);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    const submit = async () => {
        if (!fullName.trim()) {
            setErr("Full name is required.");
            return;
        }
        setSaving(true);
        setErr("");
        try {
            const { data } = await api.post(`/customers/${customerId}/contacts`, {
                full_name: fullName.trim(),
                email: email.trim(),
                direct_phone: phone.trim(),
                is_primary: isPrimary,
                active: true,
            });
            onAdded(data.contact, data.customer);
        } catch (e) {
            setErr(formatApiError(e?.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal title="Add Contact" onClose={onClose} testId="add-contact-modal">
            <div className="space-y-3">
                {err && (
                    <div className="text-sm border border-red-200 bg-red-50 text-red-700 rounded-sm px-3 py-2">
                        {err}
                    </div>
                )}
                <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                        Full Name <span className="text-red-600">*</span>
                    </label>
                    <input
                        autoFocus
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm"
                        data-testid="contact-full-name"
                    />
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                            Email
                        </label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm"
                            data-testid="contact-email"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                            Direct Phone
                        </label>
                        <input
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm odr-mono"
                            data-testid="contact-phone"
                        />
                    </div>
                </div>
                <label className="inline-flex items-center gap-2 text-sm text-gray-800">
                    <input
                        type="checkbox"
                        checked={isPrimary}
                        onChange={(e) => setIsPrimary(e.target.checked)}
                        data-testid="contact-primary"
                    />
                    Set as primary contact
                </label>
                <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={onClose}
                        className="odr-btn rounded-sm border border-gray-300 px-4 text-sm text-gray-700 hover:bg-gray-50"
                        data-testid="contact-cancel"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={saving}
                        className="odr-btn rounded-sm bg-[var(--odr-red)] hover:bg-[var(--odr-red-hover)] text-white text-sm font-semibold px-4 disabled:opacity-60"
                        data-testid="contact-save"
                    >
                        {saving ? "Saving…" : "Save Contact"}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

